/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import I18N from '@leverege/i18n';
import { Config } from '@leverege/plugin';
import { CompositeTextInput, Flex, Form, Button, Content, Pane, Text } from '@leverege/ui-elements';
import { TitleBar } from '@leverege/ui-plugin';
import { ValueCache } from '@leverege/value-cache';
import Unit from '@leverege/unit';
import { BlueprintAttributeFormValidatorFactory } from "./validators/Factory.js";
import { BlueprintAttributeFormEditorFactory } from "./editors/Factory.js";
import FormUtils from "./Utils.js";
import S from './BulkUpdateForm.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('molten.imagine.forms.BulkUpdateForm');
export default class BulkUpdateForm extends React.Component {
  static propTypes = {
    getAttributes: PropTypes.func,
    titleVariant: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    titleIcon: PropTypes.string,
    eventData: PropTypes.any,
    cancelText: PropTypes.string,
    nextText: PropTypes.string,
    backText: PropTypes.string,
    submitText: PropTypes.string
  };
  static defaultProps = {
    getAttributes: FormUtils.defaultGetAttributes,
    titleVariant: 'dialogTitle',
    get title() {
      return t('title');
    },
    get titleIcon() {
      return tIcon('titleIcon', 'fa fa-pencil fa-fw');
    },
    eventData: null,
    get cancelText() {
      return t('cancelText');
    },
    get nextText() {
      return t('nextText');
    },
    get backText() {
      return t('backText');
    },
    get submitText() {
      return t('saveText');
    }
  };
  constructor(props) {
    super(props);
    this.state = {
      breakpoint: this.getBreakPoint(window?.innerWidth),
      search: '',
      tab: 0
    };
    this.cache = new ValueCache();
  }
  getBreakPoint(size) {
    return size > Config.get('ResponsiveUtility', 'smallBreakpoint', 1024) ? 'big' : 'small';
  }
  onKeyPress = evt => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
    }
  };
  onResize = evt => {
    this.setState(state => {
      const newState = this.getBreakPoint(evt.target?.innerWidth);
      if (newState !== state.breakpoint) {
        return {
          breakpoint: newState
        };
      }
      return null;
    });
  };
  componentDidMount() {
    window.addEventListener('resize', this.onResize);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }
  onSearchChange = evt => {
    this.setState({
      search: evt.value || ''
    });
  };
  onCancel = evt => {
    const {
      onCancel,
      eventData
    } = this.props;
    if (onCancel) onCancel({
      data: eventData,
      original: evt
    });
  };
  onNext = () => {
    this.setState({
      tab: 1,
      search: ''
    });
  };
  onBack = () => {
    this.setState({
      tab: 0,
      search: ''
    });
  };
  onSubmit = async evt => {
    const {
      value
    } = evt;
    const {
      onSubmit,
      onClose
    } = this.props;
    const {
      tab
    } = this.state;
    if (tab === 0) {
      this.onNext();
      return;
    }
    if (tab === 1) {
      const updateData = this.reduceFormValuesIntoUpdateData(value);
      await onSubmit({
        ...evt,
        value: updateData
      });
      onClose();
    }
  };
  reduceFormValuesIntoUpdateDataPreview = values => {
    const attributes = this.getAttributes();
    const updateDataPreview = {
      edit: [],
      clear: []
    };
    attributes.forEach(attribute => {
      const enabled = values[`${attribute.id}:enabled`];
      const operation = values[`${attribute.id}:operation`];
      if (enabled) {
        updateDataPreview[operation].push(attribute.displayName);
      }
    });
    return updateDataPreview;
  };
  reduceFormValuesIntoUpdateData = values => {
    const attributes = this.getAttributes();
    const unitTypes = Unit.getUnitTypes();
    const updateData = [];
    attributes.forEach(attribute => {
      const enabled = values[`${attribute.id}:enabled`];
      const operation = values[`${attribute.id}:operation`];
      const value = values[`${attribute.id}:value`];
      if (enabled) {
        const op = operation === 'clear' ? 'remove' : 'replace';
        const path = attribute.blueprint.updatePath.replace('data/', '');
        if (unitTypes.includes(attribute.valueType)) {
          const convertedValue = Unit.convert(value.value, value.unit, attribute.baseUnit);
          updateData.push({
            attribute,
            op,
            path,
            value: convertedValue
          });
        } else {
          updateData.push({
            attribute,
            op,
            path,
            value
          });
        }
      }
    });
    return updateData;
  };
  compositeAttributeFilter = attr => {
    const {
      search
    } = this.state;
    return FormUtils.bulkUpdateAttributeFilter(attr) && (!search || attr.displayName.toLowerCase().includes(search.toLowerCase()));
  };
  getAttributes() {
    const {
      getAttributes,
      targets
    } = this.props;
    const {
      search
    } = this.state;
    const target = targets[0];
    return this.cache.calc((target, getAttributes) => {
      const attrs = getAttributes(target, this.compositeAttributeFilter) || [];
      attrs.sort(FormUtils.sort);
      return attrs;
    }, target, getAttributes, search);
  }
  getEditors() {
    const attributes = this.getAttributes();
    return attributes.map(attr => {
      const valueUpdateEditor = BlueprintAttributeFormEditorFactory.create('valueUpdate', {
        ...this.props,
        attr,
        key: attr.name
      });
      return valueUpdateEditor;
    });
  }
  getData() {
    const attributes = this.getAttributes();
    return attributes.reduce((acc, attr) => {
      return {
        ...acc,
        [`${attr.id}:enabled`]: false,
        [`${attr.id}:operation`]: attr.source.attribute?.relationshipType === 'oneToOne' ? 'clear' : 'edit',
        [`${attr.id}:value`]: attr.valueType === 'boolean' ? false : null
      };
    }, {});
  }
  buildValidator() {
    const attributes = this.getAttributes();
    const validators = {
      fields: {}
    };
    attributes.forEach(attr => {
      const validatorType = attr.blueprint?.attribute?.getTagStartingWith?.('formValidator:', true) || attr.name;
      const validator = BlueprintAttributeFormValidatorFactory.create(validatorType, attr);
      validators.fields[`${attr.id}:value`] = {
        ...validator,
        submitValidator: (v, name, values) => {
          const enabled = values[`${attr.id}:enabled`];
          const operation = values[`${attr.id}:operation`];
          const value = values[`${attr.id}:value`];
          if (enabled) {
            // enabled + edit + empty value combination invalid for all attributes.
            if (operation === 'edit' && (value === undefined || value === null || value === '')) {
              return 'Input required.';
            }
            return validator?.submitValidator(v, name, values);
          }
          return undefined;
        }
      };
    });
    validators.isValid = data => {
      return !data.hasErrors && Object.entries(data.values).some(([k, v]) => k.endsWith(':enabled') && v);
    };
    return validators;
  }
  renderForm = validator => {
    const {
      titleVariant,
      title,
      titleIcon,
      cancelText,
      nextText,
      headerVariant,
      bodyVariant,
      footerVariant
    } = this.props;
    const {
      breakpoint,
      search
    } = this.state;
    const headerVnt = headerVariant || (breakpoint === 'small' ? 'mobileItemFormHeader' : 'itemFormHeader');
    const bodyVnt = bodyVariant || (breakpoint === 'small' ? 'mobileItemFormBody' : 'itemFormBody');
    const footerVnt = footerVariant || (breakpoint === 'small' ? 'mobileItemFormFooter' : 'itemFormFooter');
    const editors = this.getEditors();
    const image = tIcon('noResults', 'https://storage.cloud.google.com/molten-ui-assets/search-no-results.png');
    return /*#__PURE__*/_jsxs(Content, {
      relative: breakpoint === 'big',
      children: [/*#__PURE__*/_jsxs(Content.Header, {
        variant: `${headerVnt}|formHeader`,
        children: [/*#__PURE__*/_jsx(TitleBar, {
          variant: titleVariant,
          title: title,
          icon: titleIcon
        }), /*#__PURE__*/_jsx(Pane, {
          variant: "formSearchBar",
          children: /*#__PURE__*/_jsx(CompositeTextInput, {
            variant: "formSearchBar",
            value: search,
            changeOnReturn: false,
            alwaysFireOnReturn: false,
            onChange: this.onSearchChange,
            hint: t('searchHint'),
            post: search ? /*#__PURE__*/_jsx(Button, {
              variant: "formSearchBarPost",
              onClick: this.onSearchChange
            }) : null
          })
        })]
      }), /*#__PURE__*/_jsxs(Content.Area, {
        variant: `${bodyVnt}|formBody`,
        className: S.form,
        children: [/*#__PURE__*/_jsx(Flex, {
          variant: "colL",
          children: editors
        }), !editors.length && /*#__PURE__*/_jsxs(Flex, {
          variant: "colXL",
          align: "center",
          children: [/*#__PURE__*/_jsx("img", {
            className: S.image,
            src: image,
            alt: "noResults"
          }), /*#__PURE__*/_jsxs(Flex, {
            variant: "colM",
            align: "flex-start",
            children: [/*#__PURE__*/_jsx(Text, {
              variant: "formNoResultsDisclaimer",
              children: "We Couldn't Find Any Matches For That!"
            }), /*#__PURE__*/_jsx(Text, {
              variant: "formNoResultsInstruction",
              children: "Please try searching for another attribute"
            })]
          })]
        })]
      }), /*#__PURE__*/_jsxs(Content.Footer, {
        variant: `${footerVnt}|formButtons`,
        layout: "flex:rowMEnd",
        children: [/*#__PURE__*/_jsx(Button, {
          variant: "secondary",
          type: "button",
          onClick: this.onCancel,
          children: cancelText
        }), /*#__PURE__*/_jsx(Form.Submit, {
          variant: "primary",
          children: nextText
        })]
      })]
    });
  };
  renderFormPreview = validator => {
    const {
      titleVariant,
      title,
      titleIcon,
      backText,
      submitText,
      headerVariant,
      bodyVariant,
      footerVariant,
      blueprint,
      targets
    } = this.props;
    const {
      breakpoint
    } = this.state;
    const headerVnt = headerVariant || (breakpoint === 'small' ? 'mobileItemFormHeader' : 'itemFormHeader');
    const bodyVnt = bodyVariant || (breakpoint === 'small' ? 'mobileItemFormBody' : 'itemFormBody');
    const footerVnt = footerVariant || (breakpoint === 'small' ? 'mobileItemFormFooter' : 'itemFormFooter');
    const preview = this.reduceFormValuesIntoUpdateDataPreview(validator.getData().values);
    return /*#__PURE__*/_jsxs(Content, {
      relative: breakpoint === 'big',
      children: [/*#__PURE__*/_jsx(Content.Header, {
        variant: `${headerVnt}|formHeader`,
        children: /*#__PURE__*/_jsx(TitleBar, {
          variant: titleVariant,
          title: title,
          icon: titleIcon
        })
      }), /*#__PURE__*/_jsx(Content.Area, {
        variant: `${bodyVnt}|formBody`,
        className: S.formBreakdown,
        children: /*#__PURE__*/_jsxs(Flex, {
          variant: "colL",
          children: [preview.edit.length > 0 && /*#__PURE__*/_jsxs(Flex, {
            variant: "colS",
            children: [/*#__PURE__*/_jsxs(Text, {
              variant: "formBreakdownBody",
              children: ["You are ", /*#__PURE__*/_jsx("span", {
                className: S.bold,
                children: "editing"
              }), " the following attributes for ", `${targets.length} ${targets.length === 1 ? blueprint.name.toLowerCase() : blueprint.namePlural.toLowerCase()}`, ":"]
            }), preview.edit.map(attrDisplayName => /*#__PURE__*/_jsx(Pane, {
              variant: "formBreakdownItem",
              children: attrDisplayName
            }, attrDisplayName))]
          }), preview.clear.length > 0 && /*#__PURE__*/_jsxs(Flex, {
            variant: "colS",
            children: [/*#__PURE__*/_jsxs(Text, {
              variant: "formBreakdownBody",
              children: ["You are ", /*#__PURE__*/_jsx("span", {
                className: S.bold,
                children: "clearing"
              }), " the following attributes for ", `${targets.length} ${targets.length === 1 ? blueprint.name.toLowerCase() : blueprint.namePlural.toLowerCase()}`, ":"]
            }), preview.clear.map(attrDisplayName => /*#__PURE__*/_jsx(Pane, {
              variant: "formBreakdownItem",
              children: attrDisplayName
            }, attrDisplayName))]
          })]
        })
      }), /*#__PURE__*/_jsxs(Content.Footer, {
        variant: `${footerVnt}|formButtons`,
        layout: "flex:rowMEnd",
        children: [/*#__PURE__*/_jsx(Button, {
          variant: "secondary",
          type: "button",
          onClick: this.onBack,
          children: backText
        }), /*#__PURE__*/_jsx(Form.Submit, {
          variant: "primary",
          children: submitText
        })]
      })]
    });
  };
  render() {
    const {
      variant
    } = this.props;
    const {
      breakpoint,
      tab
    } = this.state;
    const vnt = variant || (breakpoint === 'small' ? 'mobileItemForm' : 'itemForm');
    return /*#__PURE__*/_jsx(Pane, {
      variant: vnt,
      onKeyPress: this.onKeyPress,
      children: /*#__PURE__*/_jsx(Form, {
        className: S.fixed,
        data: this.getData() || {},
        onSubmit: this.onSubmit,
        render: tab ? this.renderFormPreview : this.renderForm,
        validator: this.buildValidator()
      })
    });
  }
}